import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React from 'react';

import { useGetBeneficiaryWalletQuery } from '@/api/v1';
import UsdtIcon from '@/assets/icons/tether.svg?react';
import UsdcIcon from '@/assets/icons/usdc.svg?react';
import { WalletAddressInput } from '@/components/molecules/WalletAddressInput';
import { useTranslator } from '@/hooks/useTranslator';
import { ChainType } from '@/types/chainType';
import { CurrencyCodeType } from '@/types/currency';

interface CurrencyConfig {
  readonly icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  readonly chain: ChainType;
  readonly chainLabel: string;
}

const currencyConfigs: Record<string, CurrencyConfig> = {
  [CurrencyCodeType.USDC]: {
    icon: UsdcIcon,
    chain: ChainType.ETH,
    chainLabel: 'Ethereum',
  },
  [CurrencyCodeType.USDT]: {
    icon: UsdtIcon,
    chain: ChainType.TRON,
    chainLabel: 'Tron',
  },
};

interface Props {
  readonly currency: CurrencyCodeType;
  readonly beneficiaryId?: string;
}

export const WalletDetails: React.FC<Props> = (props: Props): React.ReactElement => {
  const { currency, beneficiaryId } = props;
  const config = currencyConfigs[currency];

  const { data, isError, isFetching } = useGetBeneficiaryWalletQuery(
    {
      beneficiaryId: beneficiaryId ?? '',
      chain: config.chain,
    },
    {
      skip: !beneficiaryId,
    },
  );

  const walletAddress = data?.walletAddress ?? '';
  const tr = useTranslator();
  const Icon = config.icon;

  if (isFetching) {
    return (
      <Box sx={styles.container}>
        <Typography variant="body2" color="custom.LightGray">
          {tr('LOADING_PLEASE_WAIT')}&hellip;
        </Typography>
      </Box>
    );
  } else if (isError) {
    return (
      <Box sx={styles.container}>
        <Typography variant="body2" color="custom.LightGray">
          {tr('ACCOUNT_INFORMATION_ERROR')}
        </Typography>
      </Box>
    );
  } else {
    return (
      <Box sx={styles.container}>
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <Icon width={24} height={24} />
          <Typography variant="body2" color="text.primary">
            {data?.currency ?? currency}
          </Typography>
        </Stack>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label={tr('CHAIN')}
            value={config.chainLabel}
            fullWidth={true}
            slotProps={{ input: { readOnly: true } }}
          />
          <WalletAddressInput
            label={tr('ADDRESS')}
            value={walletAddress}
            disabled={true}
            fullWidth={true}
          />
        </Box>
      </Box>
    );
  }
};

const styles: Record<string, any> = {
  container: {
    mt: 1,
    px: 4,
    py: 3,
    borderRadius: 2,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
  },
};
